import type { AddressEntity } from '@/entities/address.entity'
import type { CompanyEntity } from '@/entities/company.entity'
import { ProductDataError, productDataRaw } from '@/lib/api/product-data-client'
import { registerBrowserSessionCleanup } from '@/lib/auth/browser-session-boundary'
import type { CustomerActiveSubscription } from '@/store/useGeneralStore'
import { isPageTeardownInProgress } from '@/utils/browser/page-teardown'

export interface CustomerBootstrapPayload {
  user: {
    id: number | null
    userId: string
    email: string | null
    firstname: string | null
    lastname: string | null
    role: string | null
    companyId: string | null
  } | null
  onboardingStatus: string | null
  company?: ConstructorParameters<typeof CompanyEntity>[0] | null
  primaryAddress?: ConstructorParameters<typeof AddressEntity>[0] | null
  activeSubscriptions: CustomerActiveSubscription[]
  hasWireTransferFullSuite?: boolean
}

/**
 * `status` is the HTTP status the bootstrap endpoint answered with, or
 * `NETWORK_FAILURE_STATUS` when the request never reached it at all.
 *
 * Callers branch on `instanceof CustomerBootstrapRequestError`, so anything the
 * request can fail with has to arrive as this type or it escapes every handler
 * they have — which is exactly what NONRESIDENT-DB was.
 */
export class CustomerBootstrapRequestError extends Error {
  /** No response was received, so there is no HTTP status to report. */
  static readonly NETWORK_FAILURE_STATUS = 0

  constructor(
    readonly status: number,
    options?: { cause?: unknown },
  ) {
    super(
      status === CustomerBootstrapRequestError.NETWORK_FAILURE_STATUS
        ? 'Failed to load customer bootstrap: network request failed'
        : `Failed to load customer bootstrap: ${status}`,
      options,
    )
    this.name = 'CustomerBootstrapRequestError'
  }

  /**
   * True when the browser never got a response. Retrying can help here and
   * cannot for a 4xx, so the two must stay distinguishable at the call site.
   */
  get isNetworkFailure(): boolean {
    return this.status === CustomerBootstrapRequestError.NETWORK_FAILURE_STATUS
  }

  /**
   * True when a second attempt can plausibly succeed: no response at all, or a
   * 502/503/504 — the edge or the Worker saying "not right now" (a 503 is what
   * the Worker answers when its rate limiter cannot, NONRESIDENTTAX-EQ).
   */
  get isTransient(): boolean {
    return this.isNetworkFailure || TRANSIENT_HTTP_STATUSES.has(this.status)
  }
}

const TRANSIENT_HTTP_STATUSES: ReadonlySet<number> = new Set([502, 503, 504])

/**
 * The request was never sent because the app itself is misconfigured — a
 * `ProductDataError` with status 0 that is not NETWORK, today only
 * `NOT_CONFIGURED` (`VITE_PRODUCT_DATA_URL` unset at build time).
 *
 * It stays a `CustomerBootstrapRequestError` so every caller's handler still
 * catches it, but it is neither a session answer (not a 401, so nobody is
 * signed out) nor a dropped request (no retry, no teardown filter): it is a
 * defect, so it reports (of-3 C2).
 */
export class CustomerBootstrapConfigurationError extends CustomerBootstrapRequestError {
  constructor(options?: { cause?: unknown }) {
    super(CustomerBootstrapRequestError.NETWORK_FAILURE_STATUS, options)
    this.name = 'CustomerBootstrapConfigurationError'
    this.message = 'Failed to load customer bootstrap: the app is not configured to reach ProductData'
  }

  override get isNetworkFailure(): boolean {
    return false
  }
}

/**
 * A bootstrap failure that names no defect: an expired session (401, fixed by
 * signing in again) or a request the browser cancelled while the page was
 * unloading or hidden (NONRESIDENT-DM). Callers still fail closed on these;
 * they just must not report them. Every other status and every network failure
 * on a live page still reports.
 */
export function isExpectedBootstrapFailure(error: unknown): boolean {
  if (!(error instanceof CustomerBootstrapRequestError)) return false
  if (error.status === 401) return true
  return error.isNetworkFailure && isPageTeardownInProgress()
}

const inFlightBootstrapRequests = new Map<string, Promise<CustomerBootstrapPayload>>()

/**
 * One retry, short enough that a customer waiting on the layout's loading gate
 * does not notice it, long enough to clear a momentary drop.
 *
 * Shorter on purpose than the onboarding wizard's 1000 ms
 * (`BOOTSTRAP_RETRY_DELAY_MS`, features/onboarding/pages/onboarding-page.tsx).
 * Here the whole portal waits behind a blank loading gate, and a failure
 * only renders it without customer data. The wizard already shows its chrome
 * and a skeleton, and a failure there blocks it behind an error, so it waits
 * longer before giving up.
 */
const NETWORK_RETRY_DELAY_MS = 350

/**
 * Goal 248: the bootstrap read goes straight to the Worker with the customer's
 * JWT. `productDataRaw` is used rather than `productData` because this module
 * must keep distinguishing an HTTP status from a transport failure — the retry
 * below fires only for the latter, and the layout branches on the difference.
 */
function requestBootstrapOnce(path: string): Promise<CustomerBootstrapPayload> {
  return productDataRaw(path)
    .catch((cause: unknown) => {
      // `productDataRaw` classifies a transport failure as ProductDataError
      // with code NETWORK; anything else is un-retryable from here. A missing
      // token carries the auth Worker's 401 and keeps it. A status-0 error that
      // is not NETWORK (NOT_CONFIGURED) never reached anyone, so it must not be
      // dressed up as a 401: the layout signs the customer out on a 401.
      if (cause instanceof ProductDataError && cause.code !== 'NETWORK') {
        if (cause.status === 0) {
          throw new CustomerBootstrapConfigurationError({ cause })
        }
        throw new CustomerBootstrapRequestError(cause.status, { cause })
      }
      throw new CustomerBootstrapRequestError(CustomerBootstrapRequestError.NETWORK_FAILURE_STATUS, { cause })
    })
    .then(async (response) => {
      if (!response.ok) {
        throw new CustomerBootstrapRequestError(response.status)
      }
      const body = (await response.json()) as
        { success: true; data: CustomerBootstrapPayload } | CustomerBootstrapPayload
      // The Worker answers `envelope(bootstrap)`.
      return 'success' in body && body.success ? body.data : (body as CustomerBootstrapPayload)
    })
}

/**
 * A transport-level failure is frequently transient — a Wi-Fi handoff, a dropped
 * connection, a request that raced the page's own startup. The only recovery
 * CustomerLayout has when this rejects is to drop its loading gate and render
 * the portal with no customer data in it, so paying one extra round trip before
 * doing that is plainly the better trade (NONRESIDENT-DM).
 *
 * Retry EXACTLY this class plus the transient 5xx statuses (`isTransient`)
 * and no other: any other HTTP status came from the server, and a 401 or a
 * 500 answers identically the second time.
 */
async function requestBootstrapWithRetry(url: string): Promise<CustomerBootstrapPayload> {
  try {
    return await requestBootstrapOnce(url)
  } catch (error) {
    if (!(error instanceof CustomerBootstrapRequestError) || !error.isTransient) {
      throw error
    }

    await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS))
    return await requestBootstrapOnce(url)
  }
}

/**
 * How long a primed answer waits for CustomerLayout to pick it up. The layout
 * mounts as soon as the session resolves (well under a second); anything older
 * is a payload nobody asked for, and the layout reads the Worker again.
 */
const PRIMED_BOOTSTRAP_TTL_MS = 30_000

const primedBootstrapRequests = new Map<string, { request: Promise<CustomerBootstrapPayload>; startedAtMs: number }>()
// Sign-out drops it: a same-user sign-in inside the 30s window must read the Worker again.
registerBrowserSessionCleanup(() => primedBootstrapRequests.clear())

/**
 * Start the bootstrap read before CustomerLayout exists (hard-load prefetch,
 * `layouts/customer/parallel-bootstrap-prefetch.ts`). The read needs only the
 * JWT; `userScopeKey` is its `sub`, the same Better Auth user id the layout
 * later passes. The layout's first non-forced call for that key takes this
 * request — in flight or settled, success or failure — exactly once, so a 401
 * still signs the customer out there. Never throws, never leaves an unhandled
 * rejection behind.
 */
export function primeCustomerBootstrap(userScopeKey: string): void {
  // A fresh prime, never an older one handed back to itself.
  primedBootstrapRequests.delete(userScopeKey)
  const request = fetchCustomerBootstrap(userScopeKey)
  request.catch(() => undefined)
  primedBootstrapRequests.set(userScopeKey, { request, startedAtMs: Date.now() })
}

function takePrimedBootstrap(userScopeKey: string): Promise<CustomerBootstrapPayload> | undefined {
  const primed = primedBootstrapRequests.get(userScopeKey)
  if (!primed) return undefined
  primedBootstrapRequests.delete(userScopeKey)
  return Date.now() - primed.startedAtMs <= PRIMED_BOOTSTRAP_TTL_MS ? primed.request : undefined
}

/**
 * The bootstrap read already under way for this user — primed and not yet taken, or in flight —
 * without taking it: the layout still takes the primed request exactly once. `undefined` when
 * nothing is under way. For prefetches that need an answer only the bootstrap has (an
 * entitlement) and must not start a read of their own.
 */
export function peekCustomerBootstrap(userScopeKey: string): Promise<CustomerBootstrapPayload> | undefined {
  const primed = primedBootstrapRequests.get(userScopeKey)
  if (primed && Date.now() - primed.startedAtMs <= PRIMED_BOOTSTRAP_TTL_MS) return primed.request
  return inFlightBootstrapRequests.get(`${userScopeKey}:/api/customer/bootstrap`)
}

export function fetchCustomerBootstrap(userScopeKey: string, forceRefresh = false): Promise<CustomerBootstrapPayload> {
  if (!forceRefresh) {
    const primed = takePrimedBootstrap(userScopeKey)
    if (primed) return primed
  }
  const url = `/api/customer/bootstrap${forceRefresh ? '?refresh=1' : ''}`
  // Scope the in-flight dedup by the authenticated user, not just the URL. A
  // module-level Map persists across sign-out/sign-in within one JS runtime, so
  // a URL-only key could hand user A's in-flight bootstrap response to user B
  // (cross-user state leak). See customer-bootstrap.test.ts.
  const requestKey = `${userScopeKey}:${url}`
  const existingRequest = inFlightBootstrapRequests.get(requestKey)
  if (existingRequest) {
    return existingRequest
  }

  // The dedup entry spans the retry, so a second caller arriving mid-retry joins
  // the same attempt rather than opening a competing one.
  const request = requestBootstrapWithRetry(url)

  const trackedRequest = request.finally(() => {
    if (inFlightBootstrapRequests.get(requestKey) === trackedRequest) {
      inFlightBootstrapRequests.delete(requestKey)
    }
  })

  inFlightBootstrapRequests.set(requestKey, trackedRequest)
  return trackedRequest
}
